import React, { useState } from 'react';
import { isIOS } from '../utils/platformDetect';

const DISMISS_KEY = 'healist_push_nudge_dismissed';

/**
 * Prompt for notification permission at the ONLY moment iOS can actually grant it:
 * when the app is running from the home screen as an installed PWA.
 *
 * WHY THIS EXISTS (root cause, verified in code 2026-08-06)
 * iOS grants Web Push ONLY to a home-screen-installed PWA. The single automatic path that
 * asked for permission was App.jsx's `onInstalled` handler on PWAInstallPrompt — but that
 * fires inside the SAFARI TAB, because on iOS "install" is a manual Add-to-Home-Screen
 * flow (PWAInstallPrompt.jsx:60-63) and tapping "✓ I've Installed It" is still the tab.
 * So `subscribe()` was called in the one context iOS will never honour, it threw, and
 * `.catch(console.error)` swallowed it. The user was never asked again, because the prompt
 * had already been marked as shown.
 *
 * Net effect: an iOS user got NO permission prompt at all and would reasonably conclude
 * "push doesn't work on iPhone" — which is exactly what happened. That matters more than
 * usual for this beta, whose cohort should be iOS (Android has no HHS build).
 *
 * The only other working path was the manual toggle buried in Settings
 * (SettingsTab.jsx:380), which a tester has no reason to go looking for.
 *
 * ⚠️ `Notification.requestPermission()` REQUIRES A USER GESTURE on iOS Safari. This is a
 * button for that reason — it cannot be fired automatically on mount.
 *
 * Rendered only when: supported && installed-PWA && permission === 'default' && not
 * subscribed. In a browser tab it renders NOTHING, deliberately: prompting where the
 * platform cannot grant is how the original bug read as "push is broken".
 */
export default function PushEnableNudge({ push, onEnabled }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1'
  );

  if (dismissed) return null;
  if (!push || !push.isSupported) return null;
  if (!push.isPWA) return null;                      // the whole point — see above
  if (push.isSubscribed || push.permission !== 'default') return null;

  const enable = async () => {
    setBusy(true);
    setFailed(null);
    try {
      await push.subscribe();
      if (onEnabled) onEnabled();
    } catch (e) {
      // Never fail silently — a swallowed error here is what hid this bug in the first place.
      console.error('[push] enable failed:', e);
      // Read the THROWN error, not `push.permission`: subscribe() calls setPermission(), but
      // that state has not re-rendered yet inside this handler, so `push.permission` is still
      // the pre-click value. usePushNotifications.js:162-164 throws this exact message.
      const denied = /permission denied/i.test(e?.message || '');
      setFailed(
        denied
          ? 'Notifications are blocked for Healist in your device settings. You can turn them back on there.'
          : 'We couldn’t turn on notifications just now. You can try again from Account → Settings.'
      );
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="push-nudge">
      <span className="hhs-nudge-icon">🔔</span>
      <div className="hhs-nudge-body">
        <strong>Turn on reminders</strong>
        <span>
          Get hydration, meal and medication reminders — and log them straight from the
          notification, without opening Healist.
          {isIOS && ' You’re opening Healist from your home screen, so this will work now.'}
        </span>
        {failed && <span className="push-nudge-error">{failed}</span>}
        <div className="hhs-nudge-actions">
          <button className="btn btn-primary btn-sm" onClick={enable} disabled={busy}>
            {busy ? 'Turning on…' : 'Enable notifications'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={dismiss} disabled={busy}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
